import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";

export default function AdminDocumentTemplates() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [folderFilter, setFolderFilter] = useState("");
  const [q, setQ] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);

  useEffect(() => { if (ready && !isDealerUser(user)) navigate("/dealer-login"); }, [ready, user, navigate]);

  const load = async () => {
    const [f, t] = await Promise.all([
      api.get("/admin/documents/folders"),
      api.get("/admin/documents/templates", { params: { folder: folderFilter || undefined, q: q || undefined, active_only: false } }),
    ]);
    setFolders(f.data.folders || []);
    setTemplates(t.data.items || []);
  };
  useEffect(() => { if (isDealerUser(user)) load(); /* eslint-disable-next-line */ }, [user, folderFilter]);

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await api.delete(`/admin/documents/templates/${t.id}`);
    await load();
  };
  const toggleActive = async (t) => {
    await api.put(`/admin/documents/templates/${t.id}`, { active: !t.active });
    await load();
  };

  return (
    <div className="ssc-container" style={{ paddingTop: 24, paddingBottom: 48 }} data-testid="admin-doc-templates">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
        <div>
          <Link to="/admin/crm" className="text-xs text-blue-600 hover:underline">← CRM</Link>
          <h1 className="text-2xl font-bold text-[#061426] mt-1">Document Template Manager</h1>
          <p className="text-sm text-gray-500">Upload your real dealership forms. Place fields on top. Map them to CRM data. The forms become available in every customer's Deal Jacket.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/documents/templates/bulk" className="ssc-btn-secondary" data-testid="tpl-bulk-link">Bulk Upload</Link>
          <button className="ssc-btn-secondary" onClick={() => setShowNewFolder(true)} data-testid="tpl-new-folder">+ Folder</button>
          <button className="ssc-btn-primary" onClick={() => setShowUpload(true)} data-testid="tpl-upload-open">+ Upload Template</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFolderFilter("")}
          className={`text-xs px-3 py-1 rounded-full border ${folderFilter === "" ? "bg-[#006DFF] text-white border-[#006DFF]" : "bg-white text-gray-700 border-gray-300"}`}
          data-testid="folder-all">All ({templates.length})</button>
        {folders.map(f => (
          <button key={f.name} onClick={() => setFolderFilter(f.name)}
            className={`text-xs px-3 py-1 rounded-full border ${folderFilter === f.name ? "bg-[#006DFF] text-white border-[#006DFF]" : "bg-white text-gray-700 border-gray-300"}`}
            data-testid={`folder-filter-${f.name.replace(/\s+/g, "-")}`}>{f.name}</button>
        ))}
      </div>

      <div className="flex gap-2 mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search template name, description, category…"
          className="flex-1 border border-gray-300 rounded p-2 text-sm" data-testid="tpl-search" />
        <button className="ssc-btn-secondary" onClick={load} data-testid="tpl-search-go">Search</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tpl-grid">
        {templates.map(t => <TemplateCard key={t.id} t={t} onDelete={deleteTemplate} onToggle={toggleActive} />)}
        {templates.length === 0 && <div className="md:col-span-2 lg:col-span-3 text-center text-gray-400 p-10 border-2 border-dashed rounded">No templates yet. Click "Upload Template" to add your first form.</div>}
      </div>

      {showUpload && <UploadModal folders={folders} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {showNewFolder && <NewFolderModal onClose={() => setShowNewFolder(false)} onDone={() => { setShowNewFolder(false); load(); }} />}
    </div>
  );
}

function TemplateCard({ t, onDelete, onToggle }) {
  const cover = (t.pages && t.pages[0]) ? t.pages[0].url : null;
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white flex flex-col" data-testid={`tpl-card-${t.id}`}>
      <div className="aspect-[8.5/11] mb-2 bg-gray-50 border rounded overflow-hidden flex items-center justify-center">
        {cover ? <AuthImg path={cover} alt={t.name} /> : <span className="text-xs text-gray-400">No preview</span>}
      </div>
      <div className="font-semibold text-[#061426] truncate" title={t.name}>{t.name}</div>
      <div className="text-xs text-gray-500 mb-1">{t.folder} · v{t.version} · {(t.fields || []).length} field{(t.fields || []).length === 1 ? "" : "s"}</div>
      <div className="text-xs text-gray-500 mb-2">{t.active ? <span className="text-emerald-700">Active</span> : <span className="text-gray-400">Inactive</span>}</div>
      <div className="flex gap-2 mt-auto">
        <Link to={`/admin/documents/templates/${t.id}/edit`} className="text-xs text-blue-600 hover:underline" data-testid={`tpl-edit-${t.id}`}>Edit Fields</Link>
        <button className="text-xs text-amber-700 hover:underline" onClick={() => onToggle(t)} data-testid={`tpl-toggle-${t.id}`}>{t.active ? "Deactivate" : "Activate"}</button>
        <button className="text-xs text-red-600 hover:underline ml-auto" onClick={() => onDelete(t)} data-testid={`tpl-delete-${t.id}`}>Delete</button>
      </div>
    </div>
  );
}

function AuthImg({ path, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    const token = localStorage.getItem("ssc_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.blob() : null)
      .then(b => { if (b) { const u = URL.createObjectURL(b); revoke = u; setUrl(u); } });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!url) return <span className="text-xs text-gray-400">Loading…</span>;
  return <img src={url} alt={alt} className="max-h-full max-w-full object-contain" />;
}

function UploadModal({ folders, onClose, onDone }) {
  const [name, setName] = useState("");
  const [folder, setFolder] = useState(folders[0]?.name || "Custom Forms");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async () => {
    if (!file || !name.trim()) { setErr("Name and file are required."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("name", name); fd.append("folder", folder);
      fd.append("category", category); fd.append("description", description);
      fd.append("file", file);
      const r = await api.post("/admin/documents/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(r.data.template);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Upload Template" onClose={onClose} testid="tpl-upload-modal">
      <Field label="Template name *"><input value={name} onChange={(e) => setName(e.target.value)}
        className="w-full border border-gray-300 rounded p-2 text-sm" placeholder="Test Drive Agreement"
        data-testid="tpl-upload-name" /></Field>
      <Field label="Folder">
        <select value={folder} onChange={(e) => setFolder(e.target.value)} className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="tpl-upload-folder">
          {folders.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
        </select>
      </Field>
      <Field label="Category"><input value={category} onChange={(e) => setCategory(e.target.value)}
        className="w-full border border-gray-300 rounded p-2 text-sm" placeholder="e.g. Test Drive" /></Field>
      <Field label="Description"><textarea value={description} onChange={(e) => setDescription(e.target.value)}
        className="w-full border border-gray-300 rounded p-2 text-sm" rows={2} /></Field>
      <Field label="File (PDF, JPG, PNG · up to 25MB) *">
        <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full text-sm" data-testid="tpl-upload-file" />
      </Field>
      {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
      <div className="flex justify-end gap-2 mt-3">
        <button className="ssc-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="ssc-btn-primary" onClick={upload} disabled={busy || !file || !name.trim()} data-testid="tpl-upload-submit">{busy ? "Uploading…" : "Upload"}</button>
      </div>
    </Modal>
  );
}

function NewFolderModal({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true); setErr("");
    try { await api.post("/admin/documents/folders", { name: name.trim() }); onDone(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New Folder" onClose={onClose} testid="folder-new-modal">
      <Field label="Folder name"><input value={name} onChange={(e) => setName(e.target.value)}
        className="w-full border border-gray-300 rounded p-2 text-sm" autoFocus data-testid="folder-new-name" /></Field>
      {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
      <div className="flex justify-end gap-2 mt-3">
        <button className="ssc-btn-secondary" onClick={onClose}>Cancel</button>
        <button className="ssc-btn-primary" onClick={create} disabled={busy || !name.trim()} data-testid="folder-new-submit">Create</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-lg p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-3">{title}</h2>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="mb-2"><label className="text-xs text-gray-500">{label}</label>{children}</div>;
}
